import { 
  collection, 
  query, 
  orderBy, 
  limit, 
  addDoc, 
  getDocs, 
  onSnapshot,
  serverTimestamp,
  writeBatch,
  FirestoreError
} from "firebase/firestore";
import { db } from "./authService";
import { HistoryItem, AnalysisResult, ChecklistItem } from "../types";

const COLLECTION_NAME = 'history';

// Type guards for data validation
const isChecklistItem = (item: unknown): item is ChecklistItem => {
  if (!item || typeof item !== 'object') return false;
  const obj = item as Record<string, unknown>;
  return (
    typeof obj.label === 'string' &&
    typeof obj.met === 'boolean' &&
    typeof obj.explanation === 'string'
  );
};

const isAnalysisResult = (data: unknown): data is AnalysisResult => {
  if (!data || typeof data !== 'object') return false;
  const obj = data as Record<string, unknown>;
  return (
    typeof obj.score === 'number' &&
    Array.isArray(obj.checklist) &&
    obj.checklist.every(isChecklistItem) &&
    typeof obj.overallFeedback === 'string' &&
    typeof obj.rewrittenText === 'string'
  );
};

const isHistoryData = (data: unknown): data is { 
  timestamp?: { toMillis: () => number }; 
  inputText: string; 
  result: AnalysisResult 
} => {
  if (!data || typeof data !== 'object') return false;
  const obj = data as Record<string, unknown>;
  return (
    typeof obj.inputText === 'string' &&
    isAnalysisResult(obj.result)
  );
};

/**
 * Safely transform Firestore document to HistoryItem
 */
const safeTransformHistoryDoc = (
  docId: string, 
  data: unknown
): HistoryItem | null => {
  try {
    if (!isHistoryData(data)) {
      console.warn(`Invalid history data for doc ${docId}:`, data);
      return null;
    }
    
    return {
      id: docId,
      timestamp: data.timestamp?.toMillis() || Date.now(),
      inputText: data.inputText,
      result: data.result
    };
  } catch (e) {
    console.error(`Error transforming history doc ${docId}:`, e);
    return null;
  }
};

/**
 * Handle Firestore errors gracefully
 */
const handleFirestoreError = (error: unknown, operation: string): void => {
  if (error instanceof FirestoreError) {
    switch (error.code) {
      case 'permission-denied':
        console.error(`${operation}: Permission denied. User may need to re-authenticate.`);
        break;
      case 'unavailable':
        console.error(`${operation}: Firestore unavailable. Check network connection.`);
        break;
      case 'not-found':
        console.error(`${operation}: Document not found.`);
        break;
      case 'already-exists':
        console.error(`${operation}: Document already exists.`);
        break;
      case 'resource-exhausted':
        console.error(`${operation}: Quota exceeded.`);
        break;
      default:
        console.error(`${operation}: Firestore error (${error.code}):`, error.message);
    }
  } else {
    console.error(`${operation}: Unexpected error:`, error);
  }
};

export const subscribeToHistory = (
  userId: string, 
  callback: (items: HistoryItem[]) => void
): (() => void) => {
  try {
    const q = query(
      collection(db, `users/${userId}/${COLLECTION_NAME}`),
      orderBy('timestamp', 'desc'),
      limit(50)
    );

    return onSnapshot(
      q, 
      (snapshot) => {
        const items: HistoryItem[] = [];
        
        snapshot.docs.forEach(doc => {
          const item = safeTransformHistoryDoc(doc.id, doc.data());
          if (item) {
            items.push(item);
          }
        });
        
        callback(items);
      }, 
      (error) => {
        handleFirestoreError(error, 'subscribeToHistory');
        callback([]);
      }
    );
  } catch (e) {
    console.error("Error setting up history subscription:", e);
    return () => {};
  }
};

export const saveToHistory = async (
  userId: string, 
  inputText: string, 
  result: AnalysisResult
): Promise<void> => {
  try {
    if (!inputText || typeof inputText !== 'string') {
      throw new Error('Invalid inputText');
    }
    
    if (!isAnalysisResult(result)) {
      throw new Error('Invalid analysis result');
    }
    
    // Sanitize and limit data size
    const sanitizedResult: AnalysisResult = {
      score: Math.max(0, Math.min(100, result.score)),
      checklist: result.checklist.slice(0, 20).map(check => ({
        label: String(check.label).slice(0, 200),
        met: check.met,
        explanation: String(check.explanation).slice(0, 1000)
      })),
      overallFeedback: String(result.overallFeedback).slice(0, 5000),
      highlights: result.highlights.slice(0, 50).map(h => ({
        substring: String(h.substring).slice(0, 500),
        type: h.type,
        explanation: String(h.explanation).slice(0, 500)
      })),
      rewrittenText: String(result.rewrittenText).slice(0, 10000)
    };
    
    const colRef = collection(db, `users/${userId}/${COLLECTION_NAME}`);
    await addDoc(colRef, {
      timestamp: serverTimestamp(),
      inputText: inputText.slice(0, 10000),
      result: sanitizedResult
    });
  } catch (error) {
    handleFirestoreError(error, 'saveToHistory');
    throw error;
  }
};

export const clearHistory = async (userId: string): Promise<void> => {
  try {
    const colRef = collection(db, `users/${userId}/${COLLECTION_NAME}`);
    const snapshot = await getDocs(colRef);
    
    if (snapshot.empty) return;

    // Batch delete in chunks of 500 (Firestore limit)
    const BATCH_SIZE = 500;
    const docs = snapshot.docs;
    
    for (let i = 0; i < docs.length; i += BATCH_SIZE) {
      const batch = writeBatch(db);
      const chunk = docs.slice(i, i + BATCH_SIZE);
      
      chunk.forEach((doc) => {
        batch.delete(doc.ref);
      });

      await batch.commit();
    }
  } catch (error) {
    handleFirestoreError(error, 'clearHistory');
    throw error;
  }
};

/**
 * Get history count for a user
 */
export const getHistoryCount = async (userId: string): Promise<number> => {
  try {
    const colRef = collection(db, `users/${userId}/${COLLECTION_NAME}`);
    const snapshot = await getDocs(colRef);
    return snapshot.size;
  } catch (error) {
    handleFirestoreError(error, 'getHistoryCount');
    return 0;
  }
};
